
import { useCallback, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { analytics, AnalyticsEvent, AnalyticsProperties, UserProperties } from '@/lib/analytics';

export const useAnalytics = () => {
  const { user, isAuthenticated } = useUser();
  const { consent } = useCookieConsent();

  // Set user when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      analytics.setUser(user.id);
    } else {
      analytics.setUser(null);
    }
  }, [isAuthenticated, user]);

  // Update analytics consent when consent changes
  useEffect(() => {
    analytics.setAnalyticsConsent(consent?.analytics ?? false);
  }, [consent]);

  const track = useCallback((event: AnalyticsEvent, properties?: AnalyticsProperties) => {
    analytics.track(event, properties);
  }, []);

  const identify = useCallback((userProperties: UserProperties) => {
    analytics.identify(userProperties);
  }, []);

  const page = useCallback((pageName?: string, properties?: AnalyticsProperties) => {
    analytics.page(pageName, properties);
  }, []);

  const pageLeave = useCallback(() => {
    analytics.pageLeave();
  }, []);

  const trackError = useCallback((error: Error, context?: string) => {
    analytics.trackError(error, context);
  }, []);

  const trackApiError = useCallback((endpoint: string, status: number, message: string) => {
    analytics.trackApiError(endpoint, status, message);
  }, []);

  const trackAuthError = useCallback((operation: string, error: string) => {
    analytics.trackAuthError(operation, error);
  }, []);

  // PostHog-specific features
  const getFeatureFlag = useCallback((flagKey: string) => {
    return analytics.getFeatureFlag(flagKey);
  }, []);

  const isFeatureEnabled = useCallback((flagKey: string) => {
    return analytics.isFeatureEnabled(flagKey);
  }, []);

  const startSessionRecording = useCallback(() => {
    analytics.startSessionRecording();
  }, []);

  const stopSessionRecording = useCallback(() => {
    analytics.stopSessionRecording();
  }, []);

  const setATTConsent = useCallback((hasConsent: boolean) => {
    analytics.setATTConsent(hasConsent);
  }, []);

  return {
    track,
    identify,
    page,
    pageLeave,
    trackError,
    trackApiError,
    trackAuthError,
    getFeatureFlag,
    isFeatureEnabled,
    startSessionRecording,
    stopSessionRecording,
    setATTConsent,
  };
};
